// App updates and ratings (contracts/app-updates.md): the app.ios and rating.prompt config keys as the API
// validates them, version comparison, the notes' shape, and the rating moments. Shared by the page and the mock
// (src/mocks/db.ts validates PUT /config/app.ios and rating.prompt with these schemas).

import { z } from "zod";
import { type Locale, LOCALES } from "../api/messaging";

/** A version such as 1.1 or 1.1.0 (src/domain/paywall.ts SEMVER). */
export const VERSION_RE = /^[0-9]{1,3}(\.[0-9]{1,3}){1,2}$/;

/** -1, 0 or 1: "1.10.0" > "1.9.2"; a missing patch counts as 0. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d > 0 ? 1 : -1;
  }
  return 0;
}

/** Note limits: a title ≤ 60 and up to 6 items of ≤ 140; notes for at most 40 versions. */
export const NOTE_LIMITS = { title: 60, item: 140, items: 6, versions: 40 } as const;

/** App links a note item may open (messaging.md links, as paths; the API takes any /path). */
export const NOTE_LINKS = ["/learn", "/cups", "/review", "/focus", "/brain", "/inbox", "/support", "/pro", "/arena", "/teams", "/friends", "/today"] as const;
const AppPath = z.string().max(100).regex(/^\/[A-Za-z0-9/_.-]{0,99}$/, "expected an app link such as /learn");

export const NoteItem = z.strictObject({ text: z.string().trim().min(1).max(NOTE_LIMITS.item), link: AppPath.optional() });
export type NoteItem = z.infer<typeof NoteItem>;
export const NoteEntry = z.strictObject({ title: z.string().trim().max(NOTE_LIMITS.title).optional(), items: z.array(NoteItem).max(NOTE_LIMITS.items).optional() });
export type NoteEntry = z.infer<typeof NoteEntry>;
/** One version's notes: `en` with a title and at least one item; other languages fall back to English per field. */
export const ReleaseNotes = z.partialRecord(z.enum(LOCALES), NoteEntry).refine((n) => Boolean(n.en?.title && n.en.items && n.en.items.length > 0), {
  message: 'notes need "en" with a title and at least one item',
});
/** `{ "<version>": localized { title, items } }`. */
export const Notes = z.record(z.string().regex(VERSION_RE), ReleaseNotes).refine((n) => Object.keys(n).length <= NOTE_LIMITS.versions, `notes for at most ${NOTE_LIMITS.versions} versions`);
export type Notes = z.infer<typeof Notes>;

export const AppIos = z
  .strictObject({
    auto: z.boolean(),
    latestVersion: z.string().regex(VERSION_RE, "expected a version such as 1.1.0"),
    minimumVersion: z.string().regex(VERSION_RE, "expected a version such as 1.1.0"),
    storeUrl: z.url({ protocol: /^https$/ }).max(300),
    promptEveryDays: z.int().min(1).max(90),
    notes: Notes,
  })
  .refine((v) => compareVersions(v.minimumVersion, v.latestVersion) <= 0, { message: "minimumVersion must not be above latestVersion", path: ["minimumVersion"] });
export type AppIos = z.infer<typeof AppIos>;

export const MOMENTS = ["daily_good", "streak_milestone", "level_three_star", "cup_finish", "duel_win", "badge_rare"] as const;
export type Moment = (typeof MOMENTS)[number];
export const MOMENT_LABEL: Record<Moment, string> = {
  daily_good: "Daily solved well (≥ 60% or top 200)",
  streak_milestone: "Streak 7, 30, 100",
  level_three_star: "3rd three-star level in a session",
  cup_finish: "Cup finished in the top half",
  duel_win: "Duel won",
  badge_rare: "Rare or epic badge",
};

/** `moments`: the moments the app may ask at (the contract names the key, not its shape: a list of moment ids). */
export const RatingPrompt = z.strictObject({
  enabled: z.boolean(),
  minActiveDays: z.int().min(0).max(365),
  minSessions: z.int().min(0).max(1000),
  cooldownDays: z.int().min(0).max(365),
  perVersion: z.boolean(),
  moments: z.array(z.enum(MOMENTS)).max(MOMENTS.length).refine((m) => new Set(m).size === m.length, "moments must be distinct"),
});
export type RatingPrompt = z.infer<typeof RatingPrompt>;

export const APP_CONFIG = {
  "app.ios": {
    schema: AppIos,
    default: { auto: true, latestVersion: "1.1.0", minimumVersion: "1.0.0", storeUrl: "https://apps.apple.com/us/app/iq-daily-brain-games-test/id6816461498", promptEveryDays: 3, notes: {} } as AppIos,
  },
  "rating.prompt": {
    schema: RatingPrompt,
    default: { enabled: true, minActiveDays: 3, minSessions: 5, cooldownDays: 60, perVersion: true, moments: [...MOMENTS] } as RatingPrompt,
  },
} as const;

/** Mock mode starts with notes for 1.1.0 written (the API's default has none). */
export const SEED_NOTES: Notes = {
  "1.1.0": {
    en: {
      title: "What's new in IQ Daily 1.1",
      items: [
        { text: "Learn: short stories that teach each puzzle type.", link: "/learn" },
        { text: "Weekly Cups: play ten puzzles any time over the weekend.", link: "/cups" },
        { text: "Review brings back the puzzles you missed.", link: "/review" },
        { text: "A daily brain card on Home and in a widget.", link: "/brain" },
      ],
    },
  },
};

export function readAppConfig(values: Record<string, unknown> | undefined): { ios: AppIos; rating: RatingPrompt } {
  const ios = APP_CONFIG["app.ios"].schema.safeParse(values?.["app.ios"]);
  const rating = APP_CONFIG["rating.prompt"].schema.safeParse(values?.["rating.prompt"]);
  return { ios: ios.success ? ios.data : APP_CONFIG["app.ios"].default, rating: rating.success ? rating.data : APP_CONFIG["rating.prompt"].default };
}

// ---------------------------------------------------------------- the notes editor

/**
 * One version's notes while edited: the items' links are shared by every language (set on English; the contract
 * stores `link` per language item, the panel writes the English link into each), the text is per language.
 */
export interface NoteDraft {
  links: (string | null)[];
  text: Record<Locale, { title: string; items: string[] }>;
}

export function noteDraftFrom(entry: Partial<Record<Locale, NoteEntry>> | undefined): NoteDraft {
  const en = entry?.en;
  const links = (en?.items ?? []).map((i) => i.link ?? null);
  const text = Object.fromEntries(
    LOCALES.map((l) => {
      const e = entry?.[l];
      return [l, { title: e?.title ?? "", items: links.map((_, i) => e?.items?.[i]?.text ?? "") }];
    }),
  ) as NoteDraft["text"];
  return { links, text };
}

/** The localized entry to save: English plus every language with any text; empty items dropped. */
export function noteDraftOut(d: NoteDraft): Partial<Record<Locale, NoteEntry>> {
  const out: Partial<Record<Locale, NoteEntry>> = {};
  for (const l of LOCALES) {
    const t = d.text[l];
    const items = t.items.map((text, i) => ({ text: text.trim(), ...(d.links[i] ? { link: d.links[i]! } : {}) })).filter((i) => i.text);
    if (l !== "en" && !t.title.trim() && !items.length) continue;
    out[l] = { ...(t.title.trim() ? { title: t.title.trim() } : {}), ...(items.length ? { items } : {}) };
  }
  return out;
}

/** What blocks saving a version's notes: an English title and item texts, within the limits. */
export function noteProblems(d: NoteDraft): string[] {
  const out: string[] = [];
  const en = d.text.en;
  if (!en.title.trim()) out.push("Write the English title.");
  if (!d.links.length) out.push("Add at least one item.");
  if (d.links.length > NOTE_LIMITS.items) out.push(`At most ${NOTE_LIMITS.items} items.`);
  en.items.forEach((t, i) => !t.trim() && out.push(`Item ${i + 1}: write the English text.`));
  for (const l of LOCALES) {
    const t = d.text[l];
    if (t.title.length > NOTE_LIMITS.title) out.push(`${l}: the title is at most ${NOTE_LIMITS.title} characters.`);
    t.items.forEach((x, i) => x.length > NOTE_LIMITS.item && out.push(`${l}, item ${i + 1}: at most ${NOTE_LIMITS.item} characters.`));
  }
  return [...new Set(out)];
}

/**
 * Installs (devices updated in the last 30 days) a minimum would lock out, as GET /app/versions counts its
 * belowMinimum: versions below it, plus apps before 1.1 (no version) once the minimum is 1.1.0 or more.
 */
export function belowMinimum(items: readonly { version: string | null; devices: number; players: number; share: number }[], minimum: string) {
  if (!VERSION_RE.test(minimum)) return { versions: [] as string[], devices: 0, players: 0, share: 0 };
  const nullBelow = compareVersions(minimum, "1.1.0") >= 0;
  const below = items.filter((v) => (v.version === null ? nullBelow : VERSION_RE.test(v.version) && compareVersions(v.version, minimum) < 0));
  return {
    versions: below.map((v) => v.version ?? "before 1.1"),
    devices: below.reduce((a, v) => a + v.devices, 0),
    players: below.reduce((a, v) => a + v.players, 0),
    share: below.reduce((a, v) => a + v.share, 0),
  };
}
